import { ApolloClient, DocumentNode, ObservableQuery } from '@apollo/client';

// DD-3231: the socket-server pushes ORDER_CHANGED { orderId } to every browser of an
// organization that can see the order. The order pages read orders from the normalized
// cache, so refreshing the one Order entity updates every view that shows it. Each refresh
// is a silent network-only client.query: it writes the cache without flipping any view
// into a loading state.

// Mirrors the socket contract in shared-denthub-code (src/models/socket-events.ts), which
// this app does not depend on: the event names and the message socket-server forwards
// verbatim. Change both together.
export const ORDER_CHANGED_EVENT = 'ORDER_CHANGED';
// The lab's internal order details (notes, internal files), pushed to the lab only.
export const INTERNAL_ORDER_DETAILS_CHANGED_EVENT = 'INTERNAL_ORDER_DETAILS_CHANGED';

export type OrderChangedEvent = {
    eventName?: string;
    orderId: string;
    supplierOrganizationId?: string | null;
    customerOrganizationId?: string | null;
    organizationIds?: string[];
    version?: number | null;
};

// A view opts in by describing itself in its query's context, so this module knows no page
// or query by name. An order list names its root field and the query that re-reads one of
// its rows with the list's own selection (useLiveOrderList sets this); a count view, whose
// numbers move when orders come and go, sets `counts`. A view of an order's internal
// details sets `internalDetailsOf` to the order id.
export type LiveOrdersContext = {
    liveOrders?: { listField?: string; rowQuery?: DocumentNode; counts?: boolean; internalDetailsOf?: string };
};

export const liveOrderListContext = (listField: string, rowQuery: DocumentNode): LiveOrdersContext => ({
    liveOrders: { listField, rowQuery },
});

export const liveOrderCountsContext: LiveOrdersContext = { liveOrders: { counts: true } };

export const liveInternalOrderDetailsContext = (orderId: string): LiveOrdersContext => ({
    liveOrders: { internalDetailsOf: orderId },
});

const liveOf = (observableQuery: ObservableQuery) =>
    (observableQuery.options.context as LiveOrdersContext | undefined)?.liveOrders;

export const MEMBERSHIP_REFRESH_MS = 15 * 1000;
const FIRST_MEMBERSHIP_REFRESH_MS = 1000;
// Matches MAX_ORDER_LIST_LIMIT in the backend: a longer loaded window cannot be re-read in
// one request, so it keeps only its row refreshes until the next poll.
const MAX_LIST_WINDOW = 200;

type Client = ApolloClient<unknown>;
type Data = Record<string, unknown> | undefined;

const silentQuery = (client: Client, query: DocumentNode, variables: Record<string, unknown>) =>
    client.query({ query, variables, fetchPolicy: 'network-only' }).catch((error) => {
        // A refresh that fails leaves the view as it was; the next event or poll retries.
        console.warn(`[order-live-updates] ${String(error)}`);
    });

const listOf = (observableQuery: ObservableQuery, listField: string): Array<{ _id?: string }> | undefined => {
    const data = observableQuery.getCurrentResult(false).data as Data;
    const list = data?.[listField];
    return Array.isArray(list) ? list : undefined;
};

const isLiveDetailsQuery = (observableQuery: ObservableQuery, orderId: string) =>
    observableQuery.queryName === 'order' &&
    observableQuery.variables?.orderId === orderId &&
    // An older version is a snapshot; only the live order follows changes.
    !observableQuery.variables?.version &&
    observableQuery.options.fetchPolicy !== 'no-cache';

/** Refreshes the views that currently show `orderId`. */
export const refreshOrder = (client: Client, orderId: string): Promise<unknown> => {
    const refreshes: Promise<unknown>[] = [];
    const rowQueries = new Set<DocumentNode>();
    client.getObservableQueries('active').forEach((observableQuery) => {
        if (isLiveDetailsQuery(observableQuery, orderId)) {
            // Its own document: several queries named `order` select different fields.
            refreshes.push(silentQuery(client, observableQuery.query, observableQuery.variables ?? {}));
            return;
        }
        const live = liveOf(observableQuery);
        if (
            live?.listField &&
            live.rowQuery &&
            listOf(observableQuery, live.listField)?.some((order) => order?._id === orderId)
        ) {
            rowQueries.add(live.rowQuery);
        }
    });
    rowQueries.forEach((rowQuery) => refreshes.push(silentQuery(client, rowQuery, { orderId })));
    return Promise.all(refreshes);
};

/** Re-reads the loaded window of every active list and the overview counts. */
export const refreshMembership = (client: Client): Promise<unknown> => {
    const refreshes: Promise<unknown>[] = [];
    client.getObservableQueries('active').forEach((observableQuery) => {
        const live = liveOf(observableQuery);
        if (!live?.listField && !live?.counts) return;
        const variables = { ...(observableQuery.variables ?? {}) } as Record<string, unknown>;
        const list = live.listField ? listOf(observableQuery, live.listField) : undefined;
        if (list) {
            // Lists page with fetchMore; re-read everything loaded from offset 0 so the
            // offset-0 merge replaces the whole window instead of cutting it to one page.
            if (list.length > MAX_LIST_WINDOW) return;
            variables.offset = 0;
            variables.limit = Math.max(Number(variables.limit) || 0, list.length);
        }
        refreshes.push(silentQuery(client, observableQuery.query, variables));
    });
    return Promise.all(refreshes);
};

/**
 * Calls `run` soon after the first trigger, then at most once per `intervalMs`, with one
 * trailing call when triggers arrived during the quiet period.
 */
export const createThrottle = (run: () => void, intervalMs: number, firstDelayMs: number) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastRun = -Infinity;
    const fire = () => {
        timer = null;
        lastRun = Date.now();
        run();
    };
    return {
        trigger() {
            if (timer) return;
            const wait = Math.max(firstDelayMs, lastRun + intervalMs - Date.now());
            timer = setTimeout(fire, wait);
        },
        cancel() {
            if (timer) clearTimeout(timer);
            timer = null;
        },
    };
};

/** Re-reads the views of an order's internal details. */
export const refreshInternalOrderDetails = (client: Client, orderId: string): Promise<unknown> =>
    Promise.all(
        Array.from(client.getObservableQueries('active').values())
            .filter((observableQuery) => liveOf(observableQuery)?.internalDetailsOf === orderId)
            .map((observableQuery) => silentQuery(client, observableQuery.query, observableQuery.variables ?? {})),
    );

/**
 * Re-reads every live view: after a reconnect whose missed events the server could not
 * replay, nothing is known about what changed in between.
 */
export const refreshAllLiveViews = (client: Client): Promise<unknown> => {
    const refreshes: Promise<unknown>[] = [refreshMembership(client)];
    client.getObservableQueries('active').forEach((observableQuery) => {
        const orderId = observableQuery.variables?.orderId as string | undefined;
        if (orderId && isLiveDetailsQuery(observableQuery, orderId)) {
            refreshes.push(silentQuery(client, observableQuery.query, observableQuery.variables ?? {}));
        } else if (liveOf(observableQuery)?.internalDetailsOf) {
            refreshes.push(silentQuery(client, observableQuery.query, observableQuery.variables ?? {}));
        }
    });
    return Promise.all(refreshes);
};

export const createOrderChangedHandler = (client: Client) => {
    const membership = createThrottle(
        () => void refreshMembership(client),
        MEMBERSHIP_REFRESH_MS,
        FIRST_MEMBERSHIP_REFRESH_MS,
    );
    return {
        handle(event: OrderChangedEvent) {
            if (!event?.orderId) return;
            void refreshOrder(client, event.orderId);
            membership.trigger();
        },
        handleInternalDetails(event: { orderId?: string }) {
            if (!event?.orderId) return;
            void refreshInternalOrderDetails(client, event.orderId);
        },
        handleReconnect() {
            void refreshAllLiveViews(client);
        },
        dispose() {
            membership.cancel();
        },
    };
};
